import { Button } from 'components/ui/button';
import { AppButtonProps, buttonStyle } from 'components/buttons/buttonStyle';
import React, { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';

// Types
import { CoiCheckEmailData, CoiCheckEmailForm } from 'types/coi-check';

// Store
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';

// Hooks
import { formatDate, useCoiCheckEmailSend } from 'hooks/useCoiCheckEmailSend';
import { useModal } from '../useModal';

// Components
import EmailListFormItem, { isEmail } from 'components/forms/inputs/EmailListInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import TextAreaInput from '../../forms/inputs/rhf-inputs/TextAreaInput';
import { ModalFooterSimple } from '../ModalFooterSimple';
import { ModalHeader } from '../ModalHeader';

// Utils
import useAuth from 'hooks/useAuth.ts';
import { t } from 'utils/getTranslations';

const FORM_OVERFLOW_CUSTOM_STYLE = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
};

interface Props {
    titleId?: string;
    initialData: CoiCheckEmailForm;
    onClose: () => void;
    cleanSelections?: () => void;
    coiCheckEmailData: CoiCheckEmailData[];
}

const CoiCheckEmailSendModal = (props: Props) => {
    const { user: currentUser } = useAuth();
    const handleSendEmail = useCoiCheckEmailSend();

    const { titleId = 'coi.send-email.title', onClose, initialData, coiCheckEmailData, cleanSelections } = props;

    const { handleSubmit, control, setValue, getValues } = useForm<CoiCheckEmailForm>({
        defaultValues: initialData,
        values: initialData,
    });
    const [replyTo] = useWatch({ control, name: ['replyTo'] });

    const removeEmail = (val: string, field: 'replyTo') => {
        const toState = getValues(field).filter((va) => va !== val);
        setValue(field, toState, { shouldDirty: true, shouldValidate: true });
    };

    const addEmail = (val: string, field: 'replyTo') => {
        const toState = getValues(field);
        if (isEmail(val)) toState.push(val);
        setValue(field, toState, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnEmailSendSubmit = async (formData: CoiCheckEmailForm) => {
        await handleSendEmail(formData, coiCheckEmailData);
        if (cleanSelections) {
            cleanSelections();
        }
        onClose();
    };

    let coiCheckDescriptionHeight = '190px';
    if (coiCheckEmailData.length > 1) {
        coiCheckDescriptionHeight = '300px';
    }

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-hidden p-6 max-sm:px-4">
                <div className="flex flex-row">
                    <div className="min-w-0 flex-1">
                        <DynamicMultiSelectInput
                            setValue={setValue}
                            control={control}
                            label="coi.send-email.to-colleague"
                            placeholder="coi.send-email.select-employee"
                            name="employees"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            required={true}
                        />
                        <EmailListFormItem
                            control={control}
                            name="replyTo"
                            onRemove={(val) => removeEmail(val, 'replyTo')}
                            addEmail={(val) => addEmail(val, 'replyTo')}
                            label="invoices.email.reply-to"
                            watchedValue={replyTo}
                        />
                        <div className="overflow-y-auto p-6" style={{ height: coiCheckDescriptionHeight }}>
                            <div>
                                {coiCheckEmailData.map((item: CoiCheckEmailData) => {
                                    return (
                                        <div className="my-4 block" key={item.id}>
                                            <div className="font-bold">{`${t('coi.send-email.content')}`}</div>
                                            <div>{`${t('coi.send-email.search-date')}: ${formatDate(
                                                item.checkedAt
                                            )}`}</div>
                                            <div>{`${t('coi.send-email.keyword')}: ${item.searchTerm}`}</div>
                                            <div>
                                                <Link
                                                    className="text-link underline"
                                                    to={`/coi-check/${item.id}`}
                                                    onClick={onClose}
                                                >
                                                    {`${t('coi.send-email.link')}`}
                                                </Link>
                                            </div>
                                            <div>{`${t('coi.send-email.sent-by')} ${currentUser?.name ?? '-'} ${t(
                                                'coi.send-email.source'
                                            )}`}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="my-4">
                            <TextAreaInput
                                name="additionalContent"
                                control={control}
                                label="coi.send-email.additional"
                                minRows={8}
                                maxRows={8}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ModalFooterSimple
                buttons={[
                    <Button {...buttonStyle({ variant: 'outlined', color: 'primary' })} key="cancel" onClick={onClose}>
                        {t('cancel')}
                    </Button>,

                    <Button
                        {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                        key="coi-send"
                        onClick={() => {
                            void handleSubmit(handleOnEmailSendSubmit)();
                        }}
                    >
                        {t('coi.send-email.send')}
                    </Button>,
                ]}
            />
        </>
    );
};

interface PropsButton extends AppButtonProps {
    coiCheckEmailData: CoiCheckEmailData[];
    cleanSelections?: () => void;
}

const CoiCheckEmailSendButton: React.FC<PropsButton> = (props: PropsButton) => {
    const { coiCheckEmailData, cleanSelections, children, variant, color, size, className, ...rest } = props;
    const { user } = useAuth();
    const userEmailAddress = user?.workEmailAddress || '';
    const { openModal, closeModal } = useModal();

    const openSendInvoiceModal = useCallback(() => {
        openModal(
            <CoiCheckEmailSendModal
                onClose={closeModal}
                titleId={'coi.send-email.title'}
                coiCheckEmailData={coiCheckEmailData}
                cleanSelections={cleanSelections}
                initialData={{
                    employees: [],
                    additionalContent: '',
                    replyTo: [userEmailAddress],
                }}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    }, [coiCheckEmailData, cleanSelections, closeModal, openModal, userEmailAddress]);
    return (
        <Button {...rest} {...buttonStyle({ variant, color, size }, className)} onClick={openSendInvoiceModal}>
            {children}
        </Button>
    );
};

export default CoiCheckEmailSendButton;
